import type { ParamValues } from '../types';
import { spanToMillimetres } from '../params';
import { clamp01, lerp, mod, smoothstep } from '../math/scalar';
import { hash2D } from '../math/random';
import {
  bevelRamp,
  circleRadius,
  diamondRadius,
  pitchAlongAxis,
  rotatePoint,
  squareRadius,
  waveProfile,
} from './helpers';
import { definePattern } from './registry';

/**
 * Lattice patterns: motifs repeated on a grid.
 *
 * Their cell size is declared in panel spans, so a cell is always a whole
 * fraction (or multiple) of the panel pitch. That is what keeps a pyramid from
 * being sliced in half by a panel joint, and it is why these patterns survive a
 * change of panel size without needing to be re-tuned.
 */

type CellShape = 'pyramid' | 'diamond' | 'cone' | 'hip';

interface PyramidParams extends ParamValues {
  shape: CellShape;
  cell: number;
  angle: number;
  stagger: number;
  sharpness: number;
  truncate: number;
  variation: number;
  seed: number;
}

export const pyramidsPattern = definePattern<PyramidParams>({
  id: 'pyramids',
  name: 'Pyramids',
  description:
    'A lattice of pyramids, cones or diamonds. Cell size is measured in panels, so the tips always land in the same place on every panel.',
  category: 'geometric',
  tags: ['lattice', 'faceted', 'support-free'],
  params: [
    {
      kind: 'select',
      key: 'shape',
      label: 'Cell shape',
      options: [
        { value: 'pyramid', label: 'Pyramid — square base' },
        { value: 'diamond', label: 'Diamond — rotated square base' },
        { value: 'cone', label: 'Cone — round base' },
        { value: 'hip', label: 'Hip roof — ridged' },
      ],
      default: 'pyramid',
      group: 'Shape',
    },
    {
      kind: 'span',
      key: 'cell',
      label: 'Cell size',
      min: 0.125,
      max: 4,
      step: 0.125,
      default: 0.5,
      help: 'Edge length of one cell, in panels.',
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Lattice rotation', default: 0, group: 'Shape' },
    {
      kind: 'number',
      key: 'stagger',
      label: 'Row offset',
      min: 0,
      max: 1,
      step: 0.05,
      default: 0,
      help: 'Shifts every other row sideways, in cells.',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'sharpness',
      label: 'Sharpness',
      min: 0.25,
      max: 4,
      step: 0.05,
      default: 1,
      help: 'Below 1 gives domed flanks, above 1 gives needle tips.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'truncate',
      label: 'Flat top',
      min: 0,
      max: 0.9,
      step: 0.01,
      default: 0,
      help: 'Cuts the tip off, leaving a plateau.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'variation',
      label: 'Height variation',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      help: 'Randomises the height of individual cells.',
      group: 'Detail',
    },
    { kind: 'int', key: 'seed', label: 'Seed', min: 0, max: 9999, default: 1, group: 'Detail' },
  ],
  sample(sample, params) {
    const { wall } = sample;
    const rotated = rotatePoint(sample.x, sample.y, params.angle);
    const pitch = pitchAlongAxis(wall.pitchX, wall.pitchY, params.angle);
    const cell = spanToMillimetres(params.cell, pitch);

    const gy = rotated.y / cell;
    const row = Math.floor(gy);
    const v = gy - row;

    const gx = rotated.x / cell + mod(row, 2) * params.stagger;
    const column = Math.floor(gx);
    const u = gx - column;

    let radius: number;
    switch (params.shape) {
      case 'diamond':
        radius = diamondRadius(u, v);
        break;
      case 'cone':
        radius = circleRadius(u, v);
        break;
      case 'hip':
        // A hip roof keeps a ridge line: only the shorter axis falls away.
        radius = Math.min(Math.abs(u - 0.5), Math.abs(v - 0.5)) * 2;
        break;
      case 'pyramid':
      default:
        radius = squareRadius(u, v);
        break;
    }

    let height = clamp01(1 - radius);

    if (params.truncate > 0) {
      height = clamp01(height / (1 - params.truncate));
    }
    if (params.sharpness !== 1) {
      height = Math.pow(height, params.sharpness);
    }
    if (params.variation > 0) {
      const scale = lerp(1, hash2D(column, row, params.seed), params.variation);
      height *= scale;
    }

    return clamp01(height);
  },
});

interface BricksParams extends ParamValues {
  length: number;
  height: number;
  angle: number;
  stagger: number;
  joint: number;
  bevel: number;
  variation: number;
  seed: number;
}

export const bricksPattern = definePattern<BricksParams>({
  id: 'bricks',
  name: 'Bricks',
  description:
    'Staggered rectangular tiles with chamfered edges and recessed joints — the classic stacked-stone wall.',
  category: 'geometric',
  tags: ['lattice', 'architectural', 'stacked'],
  params: [
    {
      kind: 'span',
      key: 'length',
      label: 'Brick length',
      min: 0.125,
      max: 4,
      step: 0.125,
      default: 1,
      group: 'Shape',
    },
    {
      kind: 'span',
      key: 'height',
      label: 'Course height',
      min: 0.0625,
      max: 2,
      step: 0.0625,
      default: 0.25,
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Rotation', default: 0, group: 'Shape' },
    {
      kind: 'number',
      key: 'stagger',
      label: 'Course offset',
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.5,
      help: 'Sideways shift of every other course, in brick lengths.',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'joint',
      label: 'Joint width',
      min: 0,
      max: 0.4,
      step: 0.01,
      default: 0.06,
      help: 'Recessed gap between bricks, as a fraction of the brick.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'bevel',
      label: 'Edge chamfer',
      min: 0,
      max: 0.5,
      step: 0.01,
      default: 0.12,
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'variation',
      label: 'Height variation',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.15,
      group: 'Detail',
    },
    { kind: 'int', key: 'seed', label: 'Seed', min: 0, max: 9999, default: 7, group: 'Detail' },
  ],
  sample(sample, params) {
    const { wall } = sample;
    const rotated = rotatePoint(sample.x, sample.y, params.angle);
    const brickLength = spanToMillimetres(params.length, wall.pitchX);
    const courseHeight = spanToMillimetres(params.height, wall.pitchY);

    const gy = rotated.y / courseHeight;
    const row = Math.floor(gy);
    const v = gy - row;

    const gx = rotated.x / brickLength + mod(row, 2) * params.stagger;
    const column = Math.floor(gx);
    const u = gx - column;

    // The joint eats into both ends of the cell; the remainder is the brick.
    const joint = params.joint * 0.5;
    const inner = 1 - params.joint;
    if (inner <= 0) return 0;

    const bu = (u - joint) / inner;
    const bv = (v - joint) / inner;
    if (bu < 0 || bu > 1 || bv < 0 || bv > 1) return 0;

    const face = Math.min(bevelRamp(bu, params.bevel), bevelRamp(bv, params.bevel));
    const scale = lerp(1, 0.55 + 0.45 * hash2D(column, row, params.seed), params.variation);
    return clamp01(face * scale);
  },
});

interface DomesParams extends ParamValues {
  cell: number;
  angle: number;
  stagger: number;
  coverage: number;
  flatten: number;
  variation: number;
  seed: number;
}

export const domesPattern = definePattern<DomesParams>({
  id: 'domes',
  name: 'Domes',
  description:
    'A field of hemispheres. Reads as soft bubbles at low coverage and as a honeycomb of touching caps when the coverage is pushed to 1.',
  category: 'geometric',
  tags: ['lattice', 'soft', 'bubbles'],
  params: [
    {
      kind: 'span',
      key: 'cell',
      label: 'Cell size',
      min: 0.125,
      max: 4,
      step: 0.125,
      default: 0.5,
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Lattice rotation', default: 0, group: 'Shape' },
    {
      kind: 'number',
      key: 'stagger',
      label: 'Row offset',
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.5,
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'coverage',
      label: 'Coverage',
      min: 0.2,
      max: 1.4,
      step: 0.01,
      default: 1,
      help: 'Dome diameter relative to the cell. Above 1 the caps overlap.',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'flatten',
      label: 'Flatten',
      min: 0,
      max: 0.9,
      step: 0.01,
      default: 0,
      help: 'Squashes the caps into shallow lenses.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'variation',
      label: 'Size variation',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      group: 'Detail',
    },
    { kind: 'int', key: 'seed', label: 'Seed', min: 0, max: 9999, default: 3, group: 'Detail' },
  ],
  sample(sample, params) {
    const { wall } = sample;
    const rotated = rotatePoint(sample.x, sample.y, params.angle);
    const pitch = pitchAlongAxis(wall.pitchX, wall.pitchY, params.angle);
    const cell = spanToMillimetres(params.cell, pitch);

    const gy = rotated.y / cell;
    const row = Math.floor(gy);
    const v = gy - row;
    const gx = rotated.x / cell + mod(row, 2) * params.stagger;
    const column = Math.floor(gx);
    const u = gx - column;

    const size = lerp(1, 0.4 + 0.6 * hash2D(column, row, params.seed), params.variation);
    const radius = circleRadius(u, v) / Math.max(params.coverage * size, 1e-3);
    if (radius >= 1) return 0;

    // A true spherical cap, then optionally squashed towards a lens.
    const cap = Math.sqrt(1 - radius * radius);
    return clamp01(cap * (1 - params.flatten));
  },
});

interface WeaveParams extends ParamValues {
  strand: number;
  angle: number;
  depth: number;
  gap: number;
}

export const weavePattern = definePattern<WeaveParams>({
  id: 'weave',
  name: 'Weave',
  description:
    'Interlaced strands passing over and under each other. The over/under checker follows the lattice, so the weave carries on across panel joints.',
  category: 'geometric',
  tags: ['lattice', 'textile', 'seamless'],
  params: [
    {
      kind: 'span',
      key: 'strand',
      label: 'Strand width',
      min: 0.0625,
      max: 2,
      step: 0.0625,
      default: 0.25,
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Rotation', default: 0, group: 'Shape' },
    {
      kind: 'number',
      key: 'depth',
      label: 'Under depth',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.45,
      help: 'How far the strand passing underneath sits below the top one.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'gap',
      label: 'Strand gap',
      min: 0,
      max: 0.4,
      step: 0.01,
      default: 0.05,
      group: 'Detail',
    },
  ],
  sample(sample, params) {
    const { wall } = sample;
    const rotated = rotatePoint(sample.x, sample.y, params.angle);
    const pitch = pitchAlongAxis(wall.pitchX, wall.pitchY, params.angle);
    const strand = spanToMillimetres(params.strand, pitch);

    const gx = rotated.x / strand;
    const gy = rotated.y / strand;
    const column = Math.floor(gx);
    const row = Math.floor(gy);
    const u = gx - column;
    const v = gy - row;

    const inset = params.gap * 0.5;
    const span = 1 - params.gap;
    if (span <= 0) return 0;

    // Each strand is a rounded bar; which one is on top alternates like a
    // checkerboard, which is what produces the over/under interlace.
    const horizontal = waveProfile(clamp01((v - inset) / span), 'round');
    const vertical = waveProfile(clamp01((u - inset) / span), 'round');
    const horizontalOnTop = mod(column + row, 2) === 0;

    const top = horizontalOnTop ? horizontal : vertical;
    const under = horizontalOnTop ? vertical : horizontal;

    return clamp01(Math.max(top, under * (1 - params.depth)));
  },
});

interface FacetsParams extends ParamValues {
  cell: number;
  angle: number;
  relief: number;
  seed: number;
  terrace: number;
}

export const facetsPattern = definePattern<FacetsParams>({
  id: 'facets',
  name: 'Facets',
  description:
    'Low-poly triangular facets built from a lattice of seeded corner heights. Because the corner heights come from the lattice and not from the panel, the facets continue across joints.',
  category: 'geometric',
  tags: ['low-poly', 'faceted', 'seamless'],
  params: [
    {
      kind: 'span',
      key: 'cell',
      label: 'Facet size',
      min: 0.125,
      max: 3,
      step: 0.125,
      default: 0.5,
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Rotation', default: 0, group: 'Shape' },
    {
      kind: 'number',
      key: 'relief',
      label: 'Relief spread',
      min: 0.1,
      max: 1,
      step: 0.01,
      default: 1,
      help: 'Height range the corner points are drawn from.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'terrace',
      label: 'Terracing',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      help: 'Snaps corner heights to levels, giving stepped crystal faces.',
      group: 'Detail',
    },
    { kind: 'int', key: 'seed', label: 'Seed', min: 0, max: 9999, default: 11, group: 'Detail' },
  ],
  sample(sample, params) {
    const { wall } = sample;
    const rotated = rotatePoint(sample.x, sample.y, params.angle);
    const pitch = pitchAlongAxis(wall.pitchX, wall.pitchY, params.angle);
    const cell = spanToMillimetres(params.cell, pitch);

    const gx = rotated.x / cell;
    const gy = rotated.y / cell;
    const ix = Math.floor(gx);
    const iy = Math.floor(gy);
    const u = gx - ix;
    const v = gy - iy;

    const corner = (cx: number, cy: number): number => {
      let value = hash2D(cx, cy, params.seed);
      if (params.terrace > 0) {
        const levels = Math.max(2, Math.round(lerp(64, 3, params.terrace)));
        value = Math.round(value * (levels - 1)) / (levels - 1);
      }
      return 0.5 + (value - 0.5) * params.relief;
    };

    const h00 = corner(ix, iy);
    const h10 = corner(ix + 1, iy);
    const h01 = corner(ix, iy + 1);
    const h11 = corner(ix + 1, iy + 1);

    // Each cell is split along its diagonal and interpolated barycentrically,
    // so the surface is piecewise planar — flat facets with crisp edges.
    const height =
      u + v < 1
        ? h00 * (1 - u - v) + h10 * u + h01 * v
        : h11 * (u + v - 1) + h10 * (1 - v) + h01 * (1 - u);

    return clamp01(height);
  },
});

interface RibsParams extends ParamValues {
  spacing: number;
  angle: number;
  width: number;
  bevel: number;
  reliefShape: 'flat' | 'round' | 'ramp';
}

export const ribsPattern = definePattern<RibsParams>({
  id: 'ribs',
  name: 'Ribs',
  description:
    'Parallel fluting with an explicit rib width and a flat land between the ribs — the slatted acoustic-panel look.',
  category: 'geometric',
  tags: ['linear', 'fluting', 'architectural'],
  params: [
    {
      kind: 'span',
      key: 'spacing',
      label: 'Rib spacing',
      min: 0.0625,
      max: 2,
      step: 0.0625,
      default: 0.25,
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Direction', default: 0, group: 'Shape' },
    {
      kind: 'number',
      key: 'width',
      label: 'Rib width',
      min: 0.05,
      max: 1,
      step: 0.01,
      default: 0.6,
      help: 'Fraction of the spacing taken up by the rib itself.',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'bevel',
      label: 'Edge chamfer',
      min: 0,
      max: 0.5,
      step: 0.01,
      default: 0.15,
      group: 'Detail',
    },
    {
      kind: 'select',
      key: 'reliefShape',
      label: 'Rib section',
      options: [
        { value: 'flat', label: 'Flat top' },
        { value: 'round', label: 'Rounded' },
        { value: 'ramp', label: 'Sawtooth ramp' },
      ],
      default: 'round',
      group: 'Shape',
    },
  ],
  sample(sample, params) {
    const { wall } = sample;
    const pitch = pitchAlongAxis(wall.pitchX, wall.pitchY, params.angle);
    const spacing = spanToMillimetres(params.spacing, pitch);
    const across =
      sample.x * Math.cos((params.angle * Math.PI) / 180) +
      sample.y * Math.sin((params.angle * Math.PI) / 180);

    const t = mod(across / spacing, 1);
    if (t > params.width) return 0;

    const local = t / params.width;
    switch (params.reliefShape) {
      case 'round':
        return waveProfile(local, 'round');
      case 'ramp':
        return smoothstep(0, 1, local);
      case 'flat':
      default:
        return bevelRamp(local, params.bevel);
    }
  },
});
